import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type AppendMessage,
  type ThreadMessageLike,
  type ThreadSuggestion,
} from "@assistant-ui/react";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  abortStreamingRequest,
  fetchSessionMessages,
  sendStreamingMessage,
} from "../store/slices/chatSlice";
import { chatMessageToThreadMessage } from "./messageMapper";
import type { ChatMessage } from "../store/slices/chatSlice";

// Stable empty-array reference. Without this, the `messages` selector
// returns a new `[]` literal on every render when no session is
// active, and React-Redux warns about an unstable selector causing
// unnecessary rerenders.
const EMPTY_MESSAGES: readonly ChatMessage[] = Object.freeze([]);

// SSE handling, session creation, abort, message_id swap, sentinel
// stripping, step accumulation, typewriter throttling, HTTP error
// detail (401/403/429/5xx) all stay in chatSlice's sendStreamingMessage
// thunk. This provider is purely a render adapter — it selects state
// out of Redux and exposes assistant-ui-shaped messages to <Thread/>.
export function SimpleChatRuntimeProvider({
  children,
  isRunning,
  setIsRunning,
}: {
  children: ReactNode;
  // Lifted to ChatV2 so it survives the per-session remount.
  isRunning: boolean;
  setIsRunning: (running: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((s) => s.chat.currentSession?.id ?? "");
  const messagesLoaded = useAppSelector(
    (s) => s.chat.currentSession?.messagesLoaded ?? false,
  );
  const messages = useAppSelector(
    (s) => s.chat.currentSession?.messages ?? EMPTY_MESSAGES,
  );

  // Lazy-load full message history when switching into a session whose
  // messages haven't been fetched yet — the sessions-list endpoint only
  // returns metadata. Mirrors the effect in legacy Chatbot.tsx.
  useEffect(() => {
    if (sessionId && !messagesLoaded && messages.length === 0) {
      dispatch(fetchSessionMessages(sessionId));
    }
  }, [sessionId, messagesLoaded, messages.length, dispatch]);

  const threadMessages = useMemo<readonly ThreadMessageLike[]>(
    () =>
      messages.map((m, i) =>
        chatMessageToThreadMessage(m, {
          // Only the very last assistant message can be the one currently
          // streaming; user messages and earlier assistant turns are
          // settled and should always show their rating.
          isStreaming:
            isRunning && i === messages.length - 1 && m.sender === "bot",
        }),
      ),
    [messages, isRunning],
  );

  // Follow-ups from the latest assistant turn, fed to the runtime's native
  // reactive `thread.suggestions` (rendered as chips in thread.tsx).
  const suggestions = useMemo<readonly ThreadSuggestion[]>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender !== "bot") continue;
      const qs = m.metadata?.follow_up_questions;
      if (qs && qs.length > 0) {
        return qs
          .filter((q) => typeof q === "string" && q.trim())
          .map((q) => ({ prompt: q }));
      }
      break;
    }
    return [];
  }, [messages]);

  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (message.content.length !== 1 || message.content[0]?.type !== "text") {
        throw new Error("Only text content is supported");
      }
      const text = message.content[0].text;
      setIsRunning(true);
      try {
        // touchSession (bubble session to top + update preview) now
        // fires inside the chatSlice thunk right after addMessage, so
        // the sidebar updates the instant the user clicks send rather
        // than waiting for the whole stream to finish. No follow-up
        // dispatch needed here.
        await dispatch(sendStreamingMessage({ sessionId, message: text })).unwrap();
      } catch (e) {
        // Stream/HTTP errors are already surfaced through Redux
        // `state.chat.error` and rendered by <ChatComposerAbove />.
        // If we let the rejection bubble up here, assistant-ui's
        // runtime treats it as an unexpected failure (dev error
        // overlay in React 18, potential double-rendering of error
        // UI) — re-throwing solves nothing the user can act on, so
        // we swallow.
        // eslint-disable-next-line no-console
        console.warn("chat send failed — surfaced via Redux:", e);
      } finally {
        setIsRunning(false);
      }
    },
    [dispatch, sessionId, setIsRunning],
  );

  const onCancel = useCallback(async () => {
    abortStreamingRequest();
    setIsRunning(false);
  }, [setIsRunning]);

  // Must be stable identity — changing it wipes the converter cache.
  const convertMessage = useCallback((m: ThreadMessageLike) => m, []);

  const runtime = useExternalStoreRuntime<ThreadMessageLike>({
    messages: threadMessages,
    isRunning,
    onNew,
    onCancel,
    convertMessage,
    suggestions,
  });

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
